import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { memo } from 'react';

/**
 * Card de schedule
 */
const ScheduleCard = memo(function ScheduleCard({
  schedule,
  professionalName,
  placeName,
  shifts = [],
  onEdit,
  onDelete,
  onShare,
  onAddShift,
  onEditShift,
  formatShiftDays,
  formatTime,
}) {
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom noWrap>
          {schedule.name}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="primary" fontWeight="medium">
            👨‍⚕️ {professionalName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            📍 {placeName}
          </Typography>
        </Box>
        
        {schedule.description && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {schedule.description}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            label={`Agenda até ${schedule.maxBookingDaysAhead || 30} dias`}
            color="primary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Mostra ${schedule.maxAvailableDaysToShow || 7} dias`}
            color="success"
            size="small"
          />
        </Box>

        {/* Seção de Turnos */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon fontSize="small" />
              Turnos ({shifts.length})
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onAddShift(schedule);
              }}
              variant="outlined"
            >
              Novo
            </Button>
          </Box>
          
          {shifts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {shifts.slice(0, 3).map((shift) => (
                <Button
                  key={shift.id}
                  variant="outlined"
                  size="small"
                  onClick={() => onEditShift(shift)}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    opacity: shift.isActive ? 1 : 0.6,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" display="block" fontWeight="medium">
                      {formatShiftDays(shift)}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                    </Typography>
                  </Box>
                </Button>
              ))}
              {shifts.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                  + {shifts.length - 3} turno(s)
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Nenhum turno configurado
            </Typography>
          )}
        </Box>
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ShareIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onShare(schedule);
          }}
          disabled={shifts.length === 0}
          sx={{ fontWeight: 600 }}
        >
          Compartilhar
        </Button>
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(schedule);
            }}
            title="Editar agenda"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(schedule);
            }}
            title="Excluir agenda"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );
});

export default ScheduleCard;
